'use client'

import { useEffect, useState } from 'react'

import ContactWidget from './contactWidget'

const SCRIPT_ID = 'oasis-maya-widget'

// Module scope, not state: widget.js loads once per page, and its onload may land after the
// effect that added it was cleaned up (Strict Mode runs effects twice), so it reports here
let mayaUp = false
const subscribers = new Set<(up: boolean) => void>()

function setMayaUp(up: boolean) {
  mayaUp = up
  subscribers.forEach((notify) => notify(up))
}

interface MayaWidgetProps {
  src: string
}

// The contact widget shows until Maya is known to be up, so a slow or failed widget.js never leaves the page without one.
export default function MayaWidget({ src }: MayaWidgetProps) {
  const [up, setUp] = useState(mayaUp)

  useEffect(() => {
    subscribers.add(setUp)
    setUp(mayaUp)
    return () => {
      subscribers.delete(setUp)
    }
  }, [])

  useEffect(() => {
    if (!src || document.getElementById(SCRIPT_ID)) return

    const origin = new URL(src, window.location.href).origin
    // Never removed: it lives as long as the script it listens to.
    // The chat iframe sends this when Maya takes its button away (e.g. token limit).
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.origin === origin && event.data?.kind === 'agent-chat:unavailable') setMayaUp(false)
    })

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = src
    script.async = true
    // When Maya is not open it answers 200 with a comment only, so loading is no proof;
    // only the full widget.js defines window.AgentChat.
    script.onload = () => setMayaUp('AgentChat' in window)
    document.body.appendChild(script)
  }, [src])

  return up ? null : <ContactWidget />
}
